/**
 * GlobalSearchTrigger — V107.52.34
 *
 * Two variants:
 *   - "pill"  : 280px input-styled button with placeholder + ⌘K hint
 *               (desktop nav, slots between centered tabs and right cluster)
 *   - "icon"  : 40×40 icon-only button
 *               (mobile nav, slots near the hamburger)
 *
 * Both open the same shared modal via context.
 */

import { Search } from 'lucide-react';
import { useGlobalSearchContext } from './GlobalSearchProvider';

interface Props {
  variant: 'pill' | 'icon';
  className?: string;
}

export function GlobalSearchTrigger({ variant, className = '' }: Props) {
  const { open } = useGlobalSearchContext();

  // Detect Mac vs Win/Linux for the right shortcut hint
  const isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');

  if (variant === 'icon') {
    return (
      <button
        type="button"
        onClick={open}
        aria-label="Open global search"
        title="Search (⌘K)"
        className={`flex items-center justify-center w-10 h-10 rounded-lg text-theme-secondary hover:text-theme-primary hover:bg-surface-hover transition-all ${className}`}
      >
        <Search className="w-5 h-5" />
      </button>
    );
  }

  // Pill variant
  return (
    <button
      type="button"
      onClick={open}
      aria-label="Open global search"
      className={`group flex items-center gap-2 w-[260px] xl:w-[300px] h-9 px-3 rounded-lg bg-surface-hover/50 hover:bg-surface-hover border border-theme-soft hover:border-theme transition-all ${className}`}
    >
      <Search className="w-4 h-4 text-theme-muted group-hover:text-theme-tertiary transition-colors shrink-0" />
      <span className="flex-1 text-left text-xs text-theme-muted group-hover:text-theme-tertiary transition-colors truncate">
        Search anything…
      </span>
      <kbd className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-surface-card border border-theme-soft font-mono text-[9px] text-theme-tertiary">
        {isMac ? '⌘' : 'Ctrl'}
        <span>K</span>
      </kbd>
    </button>
  );
}
